import { MarkdownHint } from '../markdown-hint';
import { ListEmptyState, ListFilterStatus, useListFilters } from '../list-filter-feedback';
import { ModuleFolderTree, MODULE_DRAG_TYPE } from './module-folder-tree';
import '../../assets/entity-list.css';
import { useContentPageNavigation } from '../content-page';
import { ModuleTypeIcon } from '../module-type-icon';
import { lazy, Suspense, useEffect, useState, useContext, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DataTable, DataTablePageEvent, DataTableSortEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { ExplainedButton as Button } from '../explained-button';
import { ModuleDto } from "../../general/dtos";
import { Dropdown } from 'primereact/dropdown';
import { getPaginatorLayout, getPaginatorOptions } from '../../general/data-grid-utils';
import { Divider } from 'primereact/divider';
import { GeneralErrorDialog } from '../dialogs/error-dialog';
import { ModuleManagementDataContext, ModuleManagementDataContextValue } from '../../providers/module-management-provider';
import { DeleteEntityConfirmDialog } from '../dialogs/delete-entity-confirm';
import { UserPromptDialog } from '../dialogs/prompt-dialog';
import { ImportModuleDialog } from './import-module-dialog';
import { useUnsavedActions } from '../../general/unsaved-changes-context';

const ImportWordDialog = lazy(() => import('./import-word-dialog'));

interface ModuleListProps {
    fetchAll?: boolean;
}

export function ModuleList({ fetchAll = false }: ModuleListProps) {
    const { requestAction } = useUnsavedActions();
    const { showDetails } = useContentPageNavigation();
    const navigate = useNavigate();
    const location = useLocation();

    const ctx: ModuleManagementDataContextValue | null = useContext<ModuleManagementDataContextValue | null>(ModuleManagementDataContext);
    const { currentFolderId, setCurrentFolderId, loadAllModules, toastRef, modules, selectedModules, setSelectedModules, selectAllModules, selectNoModules, invertModuleSelections, modulesLoading, selectModule,
        fetchModules, modulesPager, modulesSorting, modulesFilters, modulesError,
        loadModule, startEditModule, startCreateModule, deleteModule } = ctx!;
    const editorReturnLocation = { ...location, state: { ...location.state,
        moduleListQuery: { pager: modulesPager, orderBy: modulesSorting, filter: modulesFilters } } };
    const [pendingFilterBy, setPendingFilterBy] = useState<string>(modulesFilters.filterBy);
    const [pendingFilterValue, setPendingFilterValue] = useState<string>(modulesFilters.filterValue);
    const listFilters = useListFilters({ name: 'startsWith', created_at: 'startsWith', updated_at: 'startsWith' });
    const clearFilters = () => {
        listFilters.clear();
        setPendingFilterBy('');
        setPendingFilterValue('');
        if (modulesFilters.filterBy && modulesFilters.filterValue !== '') {
            void fetchModules({ pager: { ...modulesPager, page: 1 }, orderBy: modulesSorting, filter: { filterBy: '', filterValue: '' } });
        }
    };
    const previousFolder = useRef(currentFolderId);

    const [lastClickedIndex, setLastClickedIndex] = useState<number | null>(null);
    const dragStartedOnControl = useRef(false);

    const [error, setError] = useState<string>('');
    const [errorTitle, setErrorTitle] = useState<string>('');
    const [errorDetails, setErrorDetails] = useState<string>('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    const [showNewModulePrompt, setShowNewModulePrompt] = useState<boolean>(false);
    const [importData, setImportData] = useState<ModuleDto | null>(null);
    const [showWordImport, setShowWordImport] = useState(false);

    useEffect(() => {
        const folderChanged = previousFolder.current !== currentFolderId;
        previousFolder.current = currentFolderId;
        fetchModules({
            pager: {
                page: folderChanged ? 1 : modulesPager.page,
                pageSize: fetchAll ? 10000 : modulesPager.pageSize,
                totalRows: 0,
                totalPages: 0
            },
            orderBy: modulesSorting,
            filter: modulesFilters
        });
    }, [currentFolderId]);

    useEffect(() => {
        if (modulesError) {
            setError(modulesError);
            setErrorTitle('Error loading modules');
            setErrorDetails('There was an unhandled error while trying to load modules. Please try again later or contact support :)');
        }
    }, [modulesError]);

    const handleRowClick = (event: any) => {
        const row: ModuleDto = event.data;
        const rowIndex: number = modules.findIndex(u => u.module_key === row.module_key) ?? -1;
        if (rowIndex === -1) return;

        const isSelected: boolean = selectedModules?.some(u => u.module_key === row.module_key) ?? false;

        const originalEvent = event.originalEvent as MouseEvent;
        const isCtrl = originalEvent.ctrlKey || originalEvent.metaKey;
        const isShift = originalEvent.shiftKey;

        // --- SHIFT CLICK: select range ---
        if (isShift && lastClickedIndex !== null) {
            const start = Math.min(lastClickedIndex, rowIndex);
            const end = Math.max(lastClickedIndex, rowIndex);

            const range = modules.slice(start, end + 1) ?? [];

            // merge with existing selection (no duplicates)
            const existingByKey = new Set(selectedModules.map(u => u.module_key));
            const merged = [
                ...selectedModules ?? [],
                ...range.filter(r => !existingByKey.has(r.module_key))
            ];

            setSelectedModules(merged);
            return;
        }

        // --- CTRL CLICK: toggle selection ---
        if (isCtrl) {
            if (isSelected) {
                // remove from selection
                setSelectedModules(
                    selectedModules.filter(u => u.module_key !== row.module_key)
                );
            } else {
                // add to selection
                setSelectedModules([...selectedModules, row]);
            }
            setLastClickedIndex(rowIndex);
            return;
        }

        // --- NORMAL CLICK: single select (Explorer behavior) ---
        // Always select only this row; if already selected, selection stays the same logically
        setSelectedModules([row]);
        setLastClickedIndex(rowIndex);
        showDetails();
    };

    const nameBodyTemplate = (row: ModuleDto) => {
        return (<MarkdownHint content={row.description} className="module-table-title" data-module-key={row.module_key}>
            <ModuleTypeIcon managed={row.is_managed} /><span>{row.name}</span>
        </MarkdownHint>);
    };

    const handleServerPage = (e: DataTablePageEvent) => {
        fetchModules({
            pager: {
                ...modulesPager,
                page: (e.page ?? 0) + 1,
                pageSize: e.rows
            },
            orderBy: modulesSorting,
            filter: modulesFilters
        });
    };

    const handleServerSort = (e: DataTableSortEvent) => {
        fetchModules({
            pager: { ...modulesPager, page: 1 },
            orderBy: {
                orderBy: e.sortField,
                orderDirection: e.sortOrder === 1 ? "asc" : "desc"
            },
            filter: modulesFilters
        });
    };

    const renderFilters = () => {
        return (<>
            <div className="filter-controls">
                <span className="filter-title">Filter:</span>
                <Dropdown
                    aria-label="Module filter field"
                    value={pendingFilterBy}
                    options={[
                        { label: "None", value: "None" },
                        { label: "Name", value: "name" },
                        { label: "Created", value: "created_at" },
                        { label: "Updated", value: "updated_at" }
                    ]}
                    onChange={(e) => {
                        const newField: string = e.value;
                        setPendingFilterBy(newField);   

                        if (newField === "None") {
                            setPendingFilterBy("");
                            setPendingFilterValue("");
                            fetchModules({
                                pager: { ...modulesPager, page: 1 },
                                orderBy: modulesSorting,
                                filter: { filterBy: "", filterValue: "" }
                            });
                            return;
                        }
                    }}
                    placeholder="Field"
                    className="filter-field"
                />
                
                <InputText
                    value={pendingFilterValue}
                    onChange={(e) => setPendingFilterValue(e.target.value)}
                    placeholder="Search substring..."
                    className="filter-input"
                    disabled={!pendingFilterBy || pendingFilterBy === "None"}
                />

                <Button
                    label="Apply" className="p-button-text p-button-sm" disabled={!pendingFilterBy || pendingFilterBy === "None"}
                    disabledReason="Choose a filter field first."
                    onClick={() => {
                        fetchModules({
                            pager: { ...modulesPager, page: 1 },
                            orderBy: modulesSorting,
                            filter: { filterBy: pendingFilterBy, filterValue: pendingFilterValue }
                        });
                    }}
                />
            </div>
        </>);
    }

    function validateModuleSchema(data: any): string[] {
        const errors: string[] = [];

        // Helper to log errors
        const addError = (msg: string) => errors.push(msg);

        // Root validation
        if (!data.module_key) addError("Root module is missing 'module_key'.");
        if (!data.name) addError("Root module is missing 'name'.");

        // Fragments validation
        if (!Array.isArray(data.fragments)) {
            addError("Module must contain a 'fragments' array.");
        } else {
            data.fragments.forEach((frag: any, idx: number) => {
                if (!frag.key) addError(`Fragment[${idx}]: Missing 'key'.`);
                if (typeof frag.order !== 'number') addError(`Fragment[${idx}]: 'order' must be a number.`);
                if (!frag.html) addError(`Fragment[${idx}]: 'html' content is required.`);
                
                // Actions validation (Nested)
                if (frag.actions) {
                    frag.actions.forEach((act: any, aIdx: number) => {
                        if (!act.key) addError(`Fragment[${idx}] Action[${aIdx}]: Missing 'key'.`);
                    });
                }
            });
        }

        return errors;
    }

    async function handleImportButtonClick() {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'application/json';

        input.onchange = async (e) => {
            const target = e.target as HTMLInputElement;
            const file = target.files ? target.files[0] : null;
            if (!file) return;

            const reader = new FileReader();
            reader.onload = async (event) => {
                try {
                    const readerInstance = event.target as FileReader;
                    if (readerInstance && typeof readerInstance.result === 'string') {
                        const json = JSON.parse(readerInstance.result);
                        const errors = validateModuleSchema(json);
                        if (errors.length > 0) {
                            setError('There were errors validating the JSON file. Please reivew, fix, and try again.');
                            setErrorTitle('JSON validation errors');
                            setErrorDetails(errors.join('<br/>'));
                        } else {
                            setImportData(json as ModuleDto);
                        }
                    }

                } catch (err) {
                    console.error("Failed to parse/validate module JSON:", err);
                    alert("The selected file is not a valid managed module.");
                }
            };
            reader.readAsText(file);
        };

        input.click();
    }

    const rowsPerPageOptions = getPaginatorOptions(modulesPager!);
    const paginatorLayout = getPaginatorLayout(modulesPager!);
    const hasServerFilter = !!modulesFilters.filterBy && modulesFilters.filterValue !== '';
    const filterCount = listFilters.count + (hasServerFilter ? 1 : 0);
    const isAllDataLoaded: boolean = !hasServerFilter && (modules?.length ?? 0) > 0 && (modules?.length ?? 0) >= (modulesPager?.totalRows ?? 0);

    return (<>
        <div className="entity-list-container">
            <ModuleFolderTree folderId={currentFolderId} onSelect={id => {
                setLastClickedIndex(null);
                setCurrentFolderId(id);
                const params = new URLSearchParams(location.search);
                if (id === null) params.delete('folder'); else params.set('folder', String(id));
                navigate({ pathname: location.pathname, search: params.toString(), hash: location.hash }, { replace: true, state: location.state });
            }}
                selectedKeys={selectedModules.map(module => module.module_key)} onMoved={async () => {
                    setSelectedModules([]);
                    await Promise.all([
                        fetchModules({ pager: { ...modulesPager, page: 1 }, orderBy: modulesSorting, filter: modulesFilters }),
                        loadAllModules()
                    ]);
                }} />
            <div className="entity-list-toolbar module-list-toolbar admin-toolbar">
                <div className="selection-controls">
                    <Button label="All" onClick={selectAllModules} className="p-button-text p-button-sm" />
                    <Button label="None" onClick={selectNoModules} className="p-button-text p-button-sm" />
                    <Button label="Invert" onClick={invertModuleSelections} className="p-button-text p-button-sm" />
                    <Divider layout="vertical" />
                    <Button title='Create new module or managed module' label="New" icon="pi pi-plus" onClick={() => setShowNewModulePrompt(true)} className="p-button-text p-button-sm"/>
                    <Button title='Create a new managed module from an exported JSON file' label="Import..." icon="pi pi-plus" onClick={() => handleImportButtonClick()} className="p-button-text p-button-sm"/>
                    <Button title="Create a managed module from a Word document" label="Import Word..." icon="pi pi-file-word" onClick={() => requestAction(() => setShowWordImport(true))} className="p-button-text p-button-sm" />
                </div>
                { (!isAllDataLoaded || hasServerFilter) && (renderFilters())}
            </div>

            <ListFilterStatus count={filterCount} onClear={clearFilters} />
            <div className="entity-list-wrapper">
            <DataTable
                value={modules}
                loading={modulesLoading}
                // dataKey="module_key"
                onRowClick={handleRowClick}
                pt={{ bodyRow: {
                    draggable: true,
                    className: 'module-draggable-row',
                    onMouseDownCapture: (event: React.MouseEvent<HTMLTableRowElement>) => {
                        dragStartedOnControl.current = event.target instanceof Element && !!event.target.closest('button, a, input, textarea, select, [role="button"]');
                        if (!dragStartedOnControl.current) {
                            // PrimeReact's mousedown handler disables draggable unless its reorder handle was pressed.
                            // Our folder move owns the drag; normal row selection still happens on click.
                            event.currentTarget.draggable = true;
                            event.stopPropagation();
                        }
                    },
                    onDragStartCapture: (event: React.DragEvent<HTMLTableRowElement>) => {
                        // The row owns the native drag; keep PrimeReact's row-reorder handlers out of it.
                        event.stopPropagation();
                        const key = event.currentTarget.querySelector<HTMLElement>('[data-module-key]')?.dataset.moduleKey;
                        const row = modules.find(module => module.module_key === key);
                        if (!row || dragStartedOnControl.current) {
                            event.preventDefault();
                            return;
                        }
                        const dragged = selectedModules.some(module => module.module_key === key) ? selectedModules : [row];
                        window.getSelection()?.removeAllRanges();
                        event.dataTransfer.effectAllowed = 'move';
                        event.dataTransfer.setData(MODULE_DRAG_TYPE, JSON.stringify(dragged.map(module => module.module_key)));
                        event.dataTransfer.setData('text/plain', dragged.map(module => module.name).join(', '));
                    }
                } }}

                // filtering (disabled in server mode)
                filterDisplay={isAllDataLoaded ? "menu" : undefined}
                filters={listFilters.filters}
                onFilter={event => listFilters.setFilters(event.filters)}
                
                // pagination
                paginator
                lazy={!isAllDataLoaded}
                first={!isAllDataLoaded ? (modulesPager.page! - 1) * modulesPager.pageSize! : 0}
                rows={!isAllDataLoaded ? modulesPager.pageSize : modulesPager.pageSize}
                totalRecords={!isAllDataLoaded ? modulesPager.totalRows : modules.length}
                onPage={!isAllDataLoaded ? handleServerPage : undefined}

                // sorting
                sortField={!isAllDataLoaded ? modulesSorting.orderBy : undefined}
                sortOrder={!isAllDataLoaded ? (modulesSorting.orderDirection === "asc" ? 1 : -1) : undefined}
                onSort={!isAllDataLoaded ? handleServerSort : undefined}
                // removableSort

                scrollable
                scrollHeight="flex"
                rowClassName={(row: ModuleDto) => ({
                    'p-highlight': selectedModules.some(m => m.module_key === row.module_key)
                })}
                paginatorTemplate={paginatorLayout}
                rowsPerPageOptions={rowsPerPageOptions}
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                emptyMessage={<ListEmptyState filtered={filterCount > 0} empty="This folder has no modules. Create a module or move one here from another folder." noMatches="No modules in this folder match these filters." onClear={clearFilters} />}
            >
                <Column field="name" body={nameBodyTemplate} header="Module" sortable filter={isAllDataLoaded} />
                <Column field="created_at" header="Created" sortable  filter={isAllDataLoaded} />
                <Column field="updated_at" header="Updated" sortable filter={isAllDataLoaded} />
                <Column header="" body={(row: ModuleDto) => (<span className="admin-row-actions">
                        <Button icon="pi pi-pencil" onClick={() => requestAction(async () => {
                            if (row.is_managed) {
                                const module: ModuleDto | undefined = await loadModule(row.module_key);
                                if (module) {
                                    const params = new URLSearchParams(location.search);
                                    if (!params.getAll('module').includes(row.module_key)) params.set('module', row.module_key);
                                    navigate(`/admin/managedModuleEditor?module=${encodeURIComponent(row.module_key)}`, {
                                        state: { returnTo: { ...editorReturnLocation, search: `?${params.toString()}` }, fullModule: module }
                                    });
                                }
                            } else {
                                selectModule(row.module_key);
                                setSelectedModules([row]);
                                startEditModule(row.module_key);
                                showDetails();
                            }
                        })} tooltip='Edit module' aria-label="Edit module" />
                        <Button icon="pi pi-trash" onClick={() => requestAction(() => {
                            selectModule(row.module_key);
                            setSelectedModules([row]);
                            setShowDeleteConfirm(true);
                        })} tooltip='Delete module' aria-label="Delete module" />
                    </span>)}
                />

            </DataTable>
            </div>
        </div>

        <DeleteEntityConfirmDialog
            visible={showDeleteConfirm}
            onCancel={() => setShowDeleteConfirm(false)}
            onConfirm={async () => {
                deleteModule(selectedModules[0]?.module_key ?? '');
                setShowDeleteConfirm(false);
            }}
            entityType='Module'
            entityName={selectedModules[0]?.name ?? ''}
        />

        <GeneralErrorDialog
            visible={error.length > 0}
            title={errorTitle}
            message={error}
            details={errorDetails}
            onClose={() => setError('')}
        />

        {showWordImport && <Suspense fallback={<p role="status">Loading Word import…</p>}>
            <ImportWordDialog folderId={currentFolderId} onHide={() => setShowWordImport(false)} onPrepared={module => {
                setShowWordImport(false);
                navigate('/admin/managedModuleEditor', { state: { returnTo: editorReturnLocation, fullModule: module } });
            }} />
        </Suspense>}

        <ImportModuleDialog 
            visible={importData !== null}
            onHide={(dataImported: boolean) => {
                setImportData(null);
                if (dataImported) {
                    void fetchModules({ pager: { ...modulesPager, page: 1 }, orderBy: modulesSorting, filter: modulesFilters });
                    void loadAllModules();
                    toastRef?.current?.clear();
                    toastRef?.current?.show({
                        severity: "success",
                        summary: "Module import succeeded",
                        life: 4000
                    });
                }
            }}
            folderId={currentFolderId}
            moduleData={importData!}
        />

        <UserPromptDialog 
            visible={showNewModulePrompt}
            title={'Please select new module type'}
            question={`<div>Please select the new module type - ordinary or managed.</div>
                        <ul>
                            <li>Ordinary module is a module that consists of single HTML page which contains all its fragments inside and manages all transitions and user interactions on its own.</li>
                            <li>Managed module consists of a set of fragments and user configurable transitions between the fragments and actions suitable for each fragment. Each fragment is a separate HTML file that may cotain a managed form generated and owned by this platform. This makes the integration of the user actions with the site much more clear and provides more feedback for the administrators.</li>
                        </ul>
                    `}
            onClose={() => setShowNewModulePrompt(false)}
            actions={[
                <Button label="Ordinary" className="p-button-outlined" onClick={(e) => { startCreateModule(); showDetails(); setShowNewModulePrompt(false); }} />,
                <Button label="Managed" className="p-button-primary" onClick={(e) => {
                    setShowNewModulePrompt(false);
                    navigate("/admin/managedModuleEditor", { state: { returnTo: editorReturnLocation, folderId: currentFolderId } });
                }} />,
                <Button label="Cancel" icon="pi pi-thumbs-down" className="p-button-outlined" onClick={(e) => {
                    setShowNewModulePrompt(false);
                }} />
            ]}
        />
    </>);
}
